import lodashGet from "lodash/get";
import { distributeEvenly } from "lib/number";
import { Item } from "constants/product-collection";

import { AllocationBagType, getAllocationBagValue, setBagValue } from "helpers/allocation-dimension";
import { tableValueGetter, getTableValueSetter, saveToTable } from "helpers/table-bag";

export const BULK_IDENTIFIER = "BULK";
export const SIZE_TOTALS_IDENTIFIER = "SIZE_TOTALS";
export const SIZE_IDENTIFIER = "SIZE";
export const DEFAULT_VALUES_PINNED_ROW = "DEFAULTS_ROW";

export enum AllocationGridColumns {
    Size = "size",
    CostVendorPPS = "cost_vendor_pps",
    RetailVendorPPS = "retail_vendor_pps",
    UPC = "upc",
    Totals = "totals",
    NRFSizeCode = "nrf_size_codes",
    NRFSizeDescription = "nrf_size_descriptions",
}

export const detailColumnValueGetterCreator = (creatorParams) => {
    const { breadcrumb, isTotalColumn, sizes } = creatorParams;
    const sizesEnabled = !!sizes;
    const hasSizes = sizesEnabled && sizes?.length;
    return (getterParams) => {
        const { data, api, column, context } = getterParams;
        const itemId = data.rowId;

        const allocationBag = context?.allocationBagRef?.allocationBag;
        if (allocationBag) {
            if (data.type === SIZE_TOTALS_IDENTIFIER) {
                let bulkTotal = 0;
                let sizeTotal = 0;
                api.forEachNode((rowNode) => {
                    if (!sizesEnabled) {
                        if (rowNode.data.type === BULK_IDENTIFIER) {
                            bulkTotal += api.getValue(column, rowNode) || 0;
                        } else {
                            sizeTotal += api.getValue(column, rowNode) || 0;
                        }
                        return;
                    }
                    if (rowNode.data.type === SIZE_IDENTIFIER) {
                        bulkTotal += breadcrumb.reduce(
                            (acc, col) =>
                                acc + (getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, col) || 0),
                            0,
                        );
                        if (hasSizes) {
                            sizeTotal += breadcrumb.reduce((acc, col) => {
                                const sizeValue = sizes.reduce(
                                    (acc2, size) =>
                                        acc2 +
                                        (getAllocationBagValue(
                                            AllocationBagType.allocations,
                                            allocationBag,
                                            itemId,
                                            col.concat(size),
                                        ) || 0),
                                    0,
                                );
                                return acc + sizeValue;
                            }, 0);
                        }
                    }
                });
                return `${sizeTotal} / ${bulkTotal}`;
            }
            if (isTotalColumn) {
                return breadcrumb.reduce((acc, col) => {
                    let quantity = 0;
                    if (data.type === BULK_IDENTIFIER) {
                        quantity = getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, col);
                    } else {
                        if (hasSizes) {
                            quantity = sizes.reduce((total, size) => {
                                const value = getAllocationBagValue(
                                    AllocationBagType.allocations,
                                    allocationBag,
                                    itemId,
                                    col.concat([size]),
                                );
                                return total + (value ?? 0);
                            }, 0);
                        } else if (sizesEnabled) {
                            quantity = getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, col);
                        } else {
                            quantity = getAllocationBagValue(
                                AllocationBagType.allocations,
                                allocationBag,
                                itemId,
                                col.concat([data.size]),
                            );
                        }
                    }
                    return quantity ? quantity + (acc || 0) : acc;
                }, undefined);
            }
            const type =
                data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                    ? AllocationBagType.bulks
                    : AllocationBagType.allocations;
            const path =
                data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                    ? (breadcrumb as string[])
                    : (breadcrumb as string[]).concat([data.size]);
            return getAllocationBagValue(type, allocationBag, itemId, path);
        }

        if (data.type === SIZE_TOTALS_IDENTIFIER) {
            let totalBulks = 0;
            let totalQuantity = 0;
            api.forEachNode((rowNode) => {
                if (sizesEnabled) {
                    if (rowNode.data.type === SIZE_IDENTIFIER) {
                        const nodeData = rowNode.data;
                        const allocationsRef = lodashGet(nodeData, Item.ALLOCATIONS);
                        const bulksRef = lodashGet(nodeData, Item.BULKS);
                        totalBulks += (breadcrumb as string[][]).reduce(
                            (acc, col) => acc + (tableValueGetter(bulksRef.current, col) || 0),
                            0,
                        );
                        totalQuantity += hasSizes
                            ? (breadcrumb as string[][]).reduce(
                                  (acc, col) =>
                                      acc +
                                      sizes.reduce(
                                          (acc2, size) =>
                                              acc2 + (tableValueGetter(allocationsRef.current, col.concat(size)) || 0),
                                          0,
                                      ),
                                  0,
                              )
                            : 0;
                    }
                } else {
                    if (rowNode.data.type === BULK_IDENTIFIER) {
                        totalBulks += api.getValue(column, rowNode) || 0;
                    } else {
                        totalQuantity += api.getValue(column, rowNode) || 0;
                    }
                }
            });
            return sizesEnabled ? `${totalQuantity}` : `${totalQuantity} / ${totalBulks}`;
        } else if (isTotalColumn) {
            return (breadcrumb as string[][]).reduce<number | undefined>((acc, col) => {
                let quantity = 0;
                const bulksRef = lodashGet(data, Item.BULKS);
                if (data.type === BULK_IDENTIFIER) {
                    quantity = bulksRef.current ? tableValueGetter(bulksRef.current, col) : undefined;
                } else {
                    const allocationsRef = lodashGet(data, Item.ALLOCATIONS);
                    if (hasSizes) {
                        quantity = sizes.reduce((total, size) => {
                            const value = tableValueGetter(allocationsRef.current, col.concat([size]));
                            return total + (value ?? 0);
                        }, 0);
                    } else if (sizesEnabled) {
                        quantity = tableValueGetter(bulksRef.current, col);
                    } else {
                        quantity = allocationsRef.current
                            ? tableValueGetter(allocationsRef.current, col.concat([data.size]))
                            : undefined;
                    }
                }
                return quantity ? (acc || 0) + quantity : acc;
            }, undefined);
        }

        const table =
            data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                ? lodashGet(data, Item.BULKS).current
                : lodashGet(data, Item.ALLOCATIONS).current;
        const tablePaths =
            data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                ? (breadcrumb as string[])
                : (breadcrumb as string[]).concat([data.size]);
        return tableValueGetter(table, tablePaths);
    };
};

export const detailColumnValueSetterCreator = (creatorParams) => {
    const { breadcrumb, isTotalColumn, sizes, incrementLock, hasAllocationBag } = creatorParams;
    const sizesEnabled = !!sizes;
    const sizesEnabledButEmpty = sizes && sizes.length === 0;

    if (hasAllocationBag) {
        if (isTotalColumn) {
            return (params) => {
                const { data, oldValue, newValue, context } = params;
                const { onMutateBagValues } = context;

                if (oldValue === newValue) {
                    return false;
                }

                const itemId = data.rowId;
                let mutatedAllocationBag = context.allocationBagRef?.allocationBag;
                const distribution = distributeEvenly(newValue, breadcrumb.length);
                distribution.forEach((quantity, index) => {
                    const singleBreadcrumb = breadcrumb[index];
                    onMutateBagValues({
                        quantity,
                        dimensionValues: singleBreadcrumb,
                        rowId: itemId,
                        bagType: AllocationBagType.bulks,
                    });
                    mutatedAllocationBag = setBagValue(
                        AllocationBagType.bulks,
                        mutatedAllocationBag,
                        itemId,
                        singleBreadcrumb,
                        quantity,
                    );
                });

                context.allocationBagRef.allocationBag = mutatedAllocationBag;
                return true;
            };
        }
        return (params) => {
            const { oldValue, newValue, context, data: rowData } = params;
            const { onMutateBagValues } = context;
            if (oldValue === newValue) {
                return false;
            }

            const itemId = rowData.rowId;

            if (rowData.type === BULK_IDENTIFIER || sizesEnabledButEmpty) {
                onMutateBagValues({
                    quantity: newValue,
                    dimensionValues: breadcrumb,
                    rowId: itemId,
                    bagType: AllocationBagType.bulks,
                });
                context.allocationBagRef.allocationBag = setBagValue(
                    AllocationBagType.bulks,
                    context.allocationBagRef.allocationBag,
                    itemId,
                    breadcrumb,
                    newValue,
                );
            } else {
                let dimensionValues = [...breadcrumb];
                // Sizing grid with `sizes` (eg: GOA merchant) is different. Size is already included in breadcrumb.
                if (!sizesEnabled) {
                    dimensionValues = dimensionValues.concat([rowData.size]);
                }
                onMutateBagValues({
                    quantity: newValue,
                    dimensionValues,
                    rowId: itemId,
                    bagType: AllocationBagType.allocations,
                });
                context.allocationBagRef.allocationBag = setBagValue(
                    AllocationBagType.allocations,
                    context.allocationBagRef.allocationBag,
                    itemId,
                    dimensionValues,
                    newValue,
                );
            }

            return true;
        };
    }

    if (isTotalColumn) {
        return (setterParams) => {
            incrementLock?.();

            const { data: rowData, newValue } = setterParams;
            if (rowData.type !== BULK_IDENTIFIER) {
                return false;
            }
            const distribution = distributeEvenly(newValue, breadcrumb.length);
            const newTable = saveToTable(lodashGet(rowData, Item.BULKS).current, breadcrumb, distribution);
            lodashGet(rowData, Item.BULKS).current = newTable;
            return true;
        };
    }
    return getTableValueSetter(
        (newTable, setterParams) => {
            const { data: rowData } = setterParams;

            if (rowData.type === BULK_IDENTIFIER || sizesEnabledButEmpty) {
                lodashGet(rowData, Item.BULKS).current = newTable;
            } else {
                lodashGet(rowData, Item.ALLOCATIONS).current = newTable;
            }
        },
        ({ data: rowData }) => {
            incrementLock?.();

            if (rowData.type === BULK_IDENTIFIER || sizesEnabledButEmpty) {
                return lodashGet(rowData, Item.BULKS).current;
            } else {
                return lodashGet(rowData, Item.ALLOCATIONS).current;
            }
        },
        ({ data: rowData }) => {
            if (rowData.type === BULK_IDENTIFIER) {
                return breadcrumb as string[];
            } else {
                return (breadcrumb as string[]).concat(rowData.size ? [rowData.size] : []);
            }
        },
    );
};
